class CartVariantSelector {
  constructor() {
    this.processingLines = new Set();
    this.debounceTimeout = null;
    this.init();
  }

  init() {
    document.addEventListener('change', (e) => this.handleVariantChange(e));
  }

  async handleVariantChange(e) {
    const singleSelect = e.target.closest('.cart-variant-select');
    if (singleSelect) {
      await this.handleSingleOptionChange(singleSelect);
      return;
    }

    const multiSelect = e.target.closest('.cart-option-select');
    if (multiSelect) {
      this.handleMultiOptionChange(multiSelect);
      return;
    }
  }

  async handleSingleOptionChange(select) {
    const lineKey = select.dataset.lineKey;
    
    if (this.processingLines.has(lineKey)) {
      return;
    }

    const newVariantId = parseInt(select.value);
    const currentVariantId = parseInt(select.dataset.currentVariant);
    const quantity = parseInt(select.dataset.currentQuantity);

    if (newVariantId === currentVariantId) {
      return;
    }

    await this.changeVariantWithDiscountSupport({
      lineKey,
      newVariantId,
      currentVariantId,
      quantity,
      selectElement: select
    });
  }

  handleMultiOptionChange(optionSelect) {
    const container = optionSelect.closest('.cart-variant-options');
    if (!container) return;

    // Validate the selection before processing
    if (!this.validateMultiOptionSelection(container)) {
      console.log('Invalid multi-option selection, skipping');
      return;
    }

    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
      this.processMultiOptionChange(container);
    }, 500);
  }

  validateMultiOptionSelection(container) {
    const variantsScript = container.querySelector('.product-variants-data');
    if (!variantsScript) return false;

    try {
      const variantsData = JSON.parse(variantsScript.textContent);
      const optionSelects = container.querySelectorAll('.cart-option-select');
      
      const allSelectsHaveValues = Array.from(optionSelects).every(select => 
        select.value && select.value.trim() !== ''
      );
      
      if (!allSelectsHaveValues) {
        console.log('Not all option selects have values');
        return false;
      }
      
      const selectedOptions = Array.from(optionSelects).map(select => select.value.trim());
      
      const matchingVariant = variantsData.variants.find(variant => {
        if (!variant.options || variant.options.length !== selectedOptions.length) {
          return false;
        }
        
        return variant.options.every((option, index) => {
          return option.toString().trim().toLowerCase() === 
                 selectedOptions[index].toString().trim().toLowerCase();
        });
      });
      
      return !!matchingVariant;
    } catch (error) {
      console.error('Validation error:', error);
      return false;
    }
  }

  async processMultiOptionChange(container) {
    try {
      const variantsScript = container.querySelector('.product-variants-data');
      if (!variantsScript) return;

      const variantsData = JSON.parse(variantsScript.textContent);
      const optionSelects = container.querySelectorAll('.cart-option-select');
      
      const selectedOptions = Array.from(optionSelects).map(select => select.value.trim());
      
      console.log('Selected options:', selectedOptions);
      console.log('Available variants:', variantsData.variants);
      
      const newVariant = variantsData.variants.find(variant => {
        if (!variant.options || variant.options.length !== selectedOptions.length) {
          return false;
        }
        
        return variant.options.every((option, index) => {
          const variantOption = option.toString().trim().toLowerCase();
          const selectedOption = selectedOptions[index].toString().trim().toLowerCase();
          return variantOption === selectedOption;
        });
      });

      console.log('Found variant:', newVariant);

      if (!newVariant) {
        console.log('No matching variant found for:', selectedOptions);
        this.showError('Selected combination is not available');
        return;
      }

      if (!newVariant.available) {
        this.showError('Selected variant is out of stock');
        return;
      }

      if (newVariant.id === variantsData.currentVariant) {
        console.log('Same variant selected, no change needed');
        return;
      }

      console.log('Changing to variant:', newVariant.id);

      await this.changeVariantWithDiscountSupport({
        lineKey: variantsData.lineKey,
        newVariantId: newVariant.id,
        currentVariantId: variantsData.currentVariant,
        quantity: variantsData.currentQuantity,
        selectElement: container
      });
    } catch (error) {
      console.error('Multi-option change error:', error);
      this.showError('Error processing variant change: ' + error.message);
    }
  }

  async changeVariantWithDiscountSupport({ lineKey, newVariantId, currentVariantId, quantity, selectElement }) {
    this.processingLines.add(lineKey);
    
    try {
      this.showLoading(selectElement, true);
      this.showMessage('Updating variant...', 'info');
      
      const properties = this.getLineItemProperties(selectElement);

      await this.removeLineItem(lineKey);
      await this.delay(500);

      await this.addVariant({
        variantId: newVariantId,
        quantity,
        properties
      });

      this.showMessage('Processing discounts and promotions...', 'info');
      await this.waitForDiscountProcessing();

      this.showMessage('Variant updated! Refreshing cart...', 'success');
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Variant change failed:', error);
      this.handleError(error);
      
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } finally {
      // Don't remove from processing set since we're refreshing anyway
    }
  }

  async waitForDiscountProcessing() {
    await this.delay(2000);
    
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      if (cart.items && cart.items.length > 0) {
        await this.delay(1000);
      }
      
      const hasDiscountEligibleItems = cart.items.some(item => 
        item.product_type || item.vendor || item.price > 0
      );
      
      if (hasDiscountEligibleItems) {
        await this.delay(1500);
      }
      
    } catch (error) {
      console.log('Discount check failed, using standard delay');
      await this.delay(2000);
    }
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async removeLineItem(lineKey) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || 'Failed to remove item from cart');
    }

    return response.json();
  }

  async addVariant({ variantId, quantity, properties = {} }) {
    const items = [{
      id: variantId,
      quantity: quantity
    }];

    if (Object.keys(properties).length > 0) {
      items[0].properties = properties;
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Failed to add variant to cart');
    }

    return response.json();
  }

  getLineItemProperties(element) {
    const container = element.closest('[data-cart-item]');
    const propertiesScript = container?.querySelector('.line-properties-data');
    
    if (propertiesScript) {
      try {
        return JSON.parse(propertiesScript.textContent);
      } catch (e) {
        console.warn('Failed to parse line properties:', e);
      }
    }
    
    return {};
  }

  showLoading(element, show) {
    const container = element.closest('.cart-variant-selector');
    const loader = container?.querySelector('.cart-variant-loader');
    const selects = container?.querySelectorAll('select');
    
    if (loader) {
      loader.style.display = show ? 'flex' : 'none';
    }
    
    if (selects) {
      selects.forEach(select => {
        select.disabled = show;
      });
    }
  }

  showMessage(message, type = 'info') {
    const existing = document.querySelector('.cart-variant-message');
    if (existing) {
      existing.remove();
    }

    const messageDiv = document.createElement('div');
    messageDiv.className = 'cart-variant-message';
    
    const bgColor = type === 'success' ? '#d4edda' : type === 'error' ? '#f8d7da' : '#d1ecf1';
    const textColor = type === 'success' ? '#155724' : type === 'error' ? '#721c24' : '#0c5460';
    const borderColor = type === 'success' ? '#c3e6cb' : type === 'error' ? '#f5c6cb' : '#bee5eb';
    
    messageDiv.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: ${bgColor};
      border: 1px solid ${borderColor};
      color: ${textColor};
      padding: 15px 25px;
      border-radius: 6px;
      font-weight: 500;
      z-index: 10000;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      animation: slideDown 0.3s ease-out;
    `;
    messageDiv.textContent = message;

    document.body.appendChild(messageDiv);
  }

  handleError(error) {
    let message = 'Could not change variant. The page will refresh to restore your cart.';
    
    if (error.message) {
      if (error.message.includes('out of stock')) {
        message = 'Selected variant is out of stock';
      } else if (error.message.includes('inventory')) {
        message = 'Not enough inventory available';
      }
    }
    
    this.showMessage(message, 'error');
  }

  showError(message) {
    this.showMessage(message, 'error');
  }
}

(function() {
  function initSelector() {
    if (document.querySelector('.cart__contents') || document.querySelector('#cart')) {
      console.log('Cart Variant Selector initialized - Multi-option + Discount-friendly mode');
      new CartVariantSelector();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSelector);
  } else {
    initSelector();
  }
})();